import React, { useEffect, useMemo } from 'react'
import { isToday } from '../../helpers/dateHelper'
import { DateTime } from 'luxon'
import { limitDays } from '../../models/plans'
import { selectLimit } from '../../selectors/organizations'
import { Dispatch, State } from '../../store'
import { selectActiveAccountId } from '../../selectors/accounts'
import { useDispatch, useSelector } from 'react-redux'
import { List, ListItem, ListItemSecondaryAction } from '@mui/material'
import { CSVDownloadButton } from '../../buttons/CSVDownloadButton'
import { DatePicker } from '../DatePicker'
import { EventTypeFilterMenu } from './EventTypeFilterMenu'

// Earliest date the user can select in the picker: the start of the plan's retention
// window, floored at device creation. This is purely a UI bound — it is NOT sent as the
// query's minDate (see below), so it never narrows the server's "older logs hidden?" check.
const retentionStartDate = (allowedDays: number, device?: IDevice): Date | undefined => {
  if (!allowedDays) return undefined
  const cutoff = DateTime.utc().minus({ days: allowedDays }).toMillis()
  const createdAt = device?.createdAt ? new Date(device.createdAt).getTime() : undefined
  if (!createdAt || Number.isNaN(createdAt)) return new Date(cutoff)
  return new Date(Math.max(cutoff, createdAt))
}

export const EventHeader: React.FC<{ device?: IDevice }> = ({ device }) => {
  const dispatch = useDispatch<Dispatch>()
  const { fetch, set } = dispatch.logs
  const { setLogsFilter } = dispatch.ui

  const logLimit = useSelector((state: State) => selectLimit(state, undefined, 'log-limit'))
  const activeAccount = useSelector(selectActiveAccountId)
  const { events, selectedDate, eventTypes } = useSelector((state: State) => state.logs)
  const logsFilters = useSelector((state: State) => state.ui.logsFilters)

  const allowedDays = Math.max(limitDays(logLimit?.value) || 0, 0)
  const pickerMinDay = useMemo(() => retentionStartDate(allowedDays, device), [allowedDays, device?.createdAt])
  const logsFilterContext: LogsFilterContext = device ? 'device' : 'root'
  const savedEventTypes = activeAccount ? logsFilters[activeAccount]?.[logsFilterContext]?.eventTypes : undefined

  // Clear logs and fetch whenever device or account changes.
  // minDate is left undefined: the server clamps the visible window to the plan's retention
  // limit, and treats the absence of a caller minDate as "show everything you're allowed to"
  // — which is what lets it report when older logs are being withheld (the upgrade notice).
  useEffect(() => {
    set({
      after: undefined,
      eventTypes: savedEventTypes,
      events: { ...events, items: [] },
      maxDate: undefined,
      selectedDate: undefined,
      planUpgrade: false,
      minDate: undefined,
    })
    fetch({ deviceId: device?.id })
  }, [activeAccount, device?.id])

  const handleChangeDate = (date: Date | null | undefined) => {
    // set to end of day
    date?.setHours(23, 59, 59, 999)
    date = date || undefined
    set({
      selectedDate: date,
      after: undefined,
      maxDate: date && isToday(date) ? undefined : date,
      events: { ...events, items: [] },
      planUpgrade: false,
    })
    fetch({ deviceId: device?.id })
  }

  const fetchCsvUrl = () => dispatch.logs.fetchUrl(device?.id)

  const handleChangeEventTypes = (nextEventTypes?: IEventType[]) => {
    set({
      eventTypes: nextEventTypes,
      after: undefined,
      events: { ...events, items: [] },
      planUpgrade: false,
    })
    setLogsFilter({ accountId: activeAccount, context: logsFilterContext, eventTypes: nextEventTypes })
    fetch({ deviceId: device?.id })
  }

  return (
    <List disablePadding>
      <ListItem dense>
        <DatePicker onChange={handleChangeDate} minDay={pickerMinDay} selectedDate={selectedDate} />
        <ListItemSecondaryAction>
          <EventTypeFilterMenu device={device} value={eventTypes} onChange={handleChangeEventTypes} />
          <CSVDownloadButton fetchUrl={fetchCsvUrl} />
        </ListItemSecondaryAction>
      </ListItem>
    </List>
  )
}
